/**
 * 16:9 template-driven editor canvas: merged elements, floating element toolbar,
 * inline text editing, full-frame asset fills.
 */

import { useEffect, useMemo, useRef, useState, useCallback, type CSSProperties } from 'react';
import { marked } from 'marked';

import { AssetBinaryImage } from '@/components/domain/AssetBinaryPreview';
import { sanitizeMarkdownHtml } from '@/utils/sanitizeMarkdownHtml';
import type { Asset, Template } from '@/types/api';

import {
  asRecord,
  DESIGN_CANVAS_WIDTH_PX,
  findAssetForFile,
  imagePreviewSrc,
  layoutStyleFromElementDef,
  sortedIndices,
  stackingZIndex,
  type AssetFileShape,
} from './elementMerge';
import { getFormatElementTypeDisplay } from './formatElementTypeConfig';
import { ElementControlsToolbar } from './ElementControlsToolbar';

marked.use({ gfm: true, breaks: true });

// ─── helpers ──────────────────────────────────────────────────────────────────

const BACKGROUND_ELEMENT_NAME = 'background';

function isBackgroundElementName(raw: string): boolean {
  return raw.trim().toLowerCase() === BACKGROUND_ELEMENT_NAME;
}

function formatCompositionDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

function parseColorToRgb(color: string): { r: number; g: number; b: number } | null {
  const trimmed = color.trim();
  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      if ([r, g, b].some((v) => Number.isNaN(v))) return null;
      return { r, g, b };
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      if ([r, g, b].some((v) => Number.isNaN(v))) return null;
      return { r, g, b };
    }
  }
  const rgbMatch = trimmed.match(/rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (!rgbMatch) return null;
  const r = Number(rgbMatch[1]);
  const g = Number(rgbMatch[2]);
  const b = Number(rgbMatch[3]);
  if ([r, g, b].some((v) => Number.isNaN(v))) return null;
  return { r, g, b };
}

function isColorDark(color: string): boolean {
  const rgb = parseColorToRgb(color);
  if (!rgb) return false;
  const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  return luminance < 0.45;
}

// ─── types ────────────────────────────────────────────────────────────────────

export interface TemplateDrivenCanvasProps {
  template: Template;
  workingElements: unknown[];
  assetsList: Asset[];
  patchEl: (index: number, fn: (el: Record<string, unknown>) => void) => void;
  onPickImage: (elementIndex: number) => void;
  onPickVideo: (elementIndex: number) => void;
}

// ─── component ────────────────────────────────────────────────────────────────

export function TemplateDrivenCanvas({
  template,
  workingElements,
  assetsList,
  patchEl,
  onPickImage,
  onPickVideo,
}: TemplateDrivenCanvasProps) {
  const paintOrder = useMemo(() => sortedIndices(template), [template]);

  const compositionDurationLabel = useMemo(() => {
    const ms = template.render_settings?.duration_ms;
    return typeof ms === 'number' && Number.isFinite(ms) ? formatCompositionDurationMs(ms) : null;
  }, [template]);
  const canvasWidthPx = template.render_settings?.width_px;
  const canvasHeightPx = template.render_settings?.height_px;

  // ── Selection state ──
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // textEditingIndex: which text element is currently in inline edit mode
  const [textEditingIndex, setTextEditingIndex] = useState<number | null>(null);
  const [canvasScale, setCanvasScale] = useState(1);

  const canvasRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const baseWidth =
      typeof canvasWidthPx === 'number' && canvasWidthPx > 0
        ? canvasWidthPx
        : DESIGN_CANVAS_WIDTH_PX;
    const updateScale = () => {
      const rect = el.getBoundingClientRect();
      if (!Number.isFinite(rect.width) || rect.width <= 0) return;
      const next = rect.width / baseWidth;
      if (Number.isFinite(next) && next > 0) {
        setCanvasScale(next);
      }
    };
    updateScale();
    const observer = new ResizeObserver(() => updateScale());
    observer.observe(el);
    return () => observer.disconnect();
  }, [canvasWidthPx]);

  // Deselect when pressing Escape
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setSelectedIndex(null);
        setTextEditingIndex(null);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Deselect when clicking outside canvas + toolbar
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      const inCanvas = canvasRef.current?.contains(target);
      const inToolbar = toolbarRef.current?.contains(target);
      const inToolbarPopover =
        target instanceof Element &&
        target.closest('[data-element-controls-popover="true"]') != null;
      if (!inCanvas && !inToolbar && !inToolbarPopover) {
        setSelectedIndex(null);
        setTextEditingIndex(null);
      }
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const handleDeselect = useCallback(() => {
    setSelectedIndex(null);
    setTextEditingIndex(null);
  }, []);

  const handleFrameClick = useCallback((i: number, typ: string) => {
    setSelectedIndex(i);
    if (typ === 'text') {
      setTextEditingIndex(i);
    } else {
      setTextEditingIndex(null);
    }
  }, []);

  return (
    <div className="aspectly:flex-1 aspectly:overflow-auto aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:p-8 aspectly:pt-0 aspectly:min-h-0 aspectly:gap-0">
      <div className="aspectly:relative aspectly:flex aspectly:w-full aspectly:max-w-5xl aspectly:flex-col aspectly:items-stretch aspectly:gap-0">
        {/* ── Floating element toolbar (above canvas) ── */}
        <div
          ref={toolbarRef}
          className="aspectly:relative aspectly:z-40 aspectly:min-h-[52px] aspectly:flex aspectly:items-center aspectly:overflow-visible"
        >
          {selectedIndex !== null ? (
            <ElementControlsToolbar
              selectedIndex={selectedIndex}
              template={template}
              workingElements={workingElements}
              assetsList={assetsList}
              patchEl={patchEl}
              onPickImage={onPickImage}
              onPickVideo={onPickVideo}
              onDeselect={handleDeselect}
            />
          ) : (
            <div className="aspectly:flex aspectly:w-full aspectly:items-center aspectly:justify-center aspectly:py-2">
              <span className="aspectly:text-[10px] aspectly:text-white/20 aspectly:italic aspectly:select-none">
                Click an element to select it
              </span>
            </div>
          )}
        </div>

        {/* ── Duration label ── */}
        {compositionDurationLabel != null ? (
          <div className="aspectly:flex aspectly:shrink-0 aspectly:justify-end aspectly:px-0.5 aspectly:pb-1">
            <div
              className="aspectly:pointer-events-none aspectly:flex aspectly:items-center aspectly:gap-1.5 aspectly:text-text-muted"
              title="Composition duration (export length)"
              aria-label={`Composition duration ${compositionDurationLabel}`}
            >
              <span
                className="material-symbols-outlined aspectly:shrink-0 aspectly:leading-none aspectly:text-text-muted"
                style={{ fontSize: '16px' }}
                aria-hidden={true}
              >
                access_time
              </span>
              <span className="aspectly:text-[10px] aspectly:font-bold aspectly:tracking-widest aspectly:uppercase">
                {compositionDurationLabel}
              </span>
            </div>
          </div>
        ) : null}

        {/* ── Canvas ── */}
        <div
          ref={canvasRef}
          className="aspectly:relative aspectly:z-0 aspectly:aspect-video aspectly:w-full aspectly:overflow-hidden aspectly:rounded-xl aspectly:border aspectly:border-white/5 aspectly:bg-card-dark aspectly:shadow-2xl"
          onClick={() => {
            // clicking canvas bg deselects
            setSelectedIndex(null);
            setTextEditingIndex(null);
          }}
        >
          {/* dot grid */}
          <div
            className="aspectly:absolute aspectly:inset-0 aspectly:opacity-10 aspectly:pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />

          {paintOrder.map((i) => {
            const def = asRecord(template.elements[i]);
            const row = asRecord(workingElements[i]);
            if (!def) return null;

            const typ = String(row?.element_type ?? def.element_type ?? '');
            const typeDisplay = getFormatElementTypeDisplay(typ);
            const name = String(row?.element_name ?? def.element_name ?? `Element ${i + 1}`);
            const isBackgroundSlot = isBackgroundElementName(name);
            const data = asRecord(row?.element_data) ?? {};
            const z = stackingZIndex(def);
            const box: CSSProperties = layoutStyleFromElementDef(
              def,
              canvasWidthPx,
              canvasHeightPx,
            );

            const isSelected = selectedIndex === i;
            const isTextEditing = textEditingIndex === i;

            // Selection ring classes
            const selectionRing = isSelected
              ? 'aspectly:ring-2 aspectly:ring-primary aspectly:ring-offset-0' : 'aspectly:ring-1 aspectly:ring-inset aspectly:ring-black/10';

            // ── color_fill ──
            if (typ === 'color_fill') {
              const c = String(asRecord(data.color_fill_element)?.color ?? '#0b1218');
              return (
                <div
                  key={`el-${i}-${name}`}
                  className={`aspectly:absolute aspectly:min-h-0 aspectly:min-w-0 aspectly:overflow-hidden aspectly:rounded-lg aspectly:cursor-pointer aspectly:transition-shadow ${selectionRing}`}
                  style={{ ...box, zIndex: z }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFrameClick(i, typ);
                  }}
                  title={`${name} (${typeDisplay.label})`}
                >
                  <div className="aspectly:absolute aspectly:inset-0" style={{ backgroundColor: c }} />
                </div>
              );
            }

            // ── background image ──
            if (isBackgroundSlot && typ === 'image') {
              const af = asRecord(asRecord(data.image_element)?.asset_file);
              const has = Boolean(af?.uri);
              const linked =
                has && af?.uri
                  ? findAssetForFile(af as unknown as AssetFileShape, assetsList)
                  : undefined;
              const srcFallback = (af?.uri as string | undefined) || imagePreviewSrc(row, def);
              return (
                <div
                  key={`el-${i}-${name}`}
                  className={`aspectly:absolute aspectly:overflow-hidden aspectly:cursor-pointer aspectly:transition-shadow ${selectionRing}`}
                  style={{ ...box, zIndex: z }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFrameClick(i, typ);
                  }}
                  title={`${name} (${typeDisplay.label})`}
                >
                  <div className="aspectly:absolute aspectly:inset-0 aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-black/20">
                    {linked?.id != null ? (
                      <AssetBinaryImage
                        assetId={linked.id}
                        endpoint={linked.thumbnail_url != null ? 'thumbnail' : 'content'}
                        remoteUrlHint={linked.thumbnail_url ?? linked.uri}
                        alt=""
                        className="aspectly:h-full aspectly:w-full aspectly:object-cover"
                        loadingClassName="aspectly:h-full aspectly:w-full aspectly:min-h-[48px] aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-black/20"
                      />
                    ) : srcFallback ? (
                      <img src={srcFallback} alt="" className="aspectly:h-full aspectly:w-full aspectly:object-cover" />
                    ) : (
                      <span className="material-symbols-outlined aspectly:text-white/20 aspectly:text-3xl">
                        image
                      </span>
                    )}
                  </div>
                </div>
              );
            }

            // ── background video ──
            if (isBackgroundSlot && typ === 'video') {
              const af = asRecord(asRecord(data.video_element)?.asset_file);
              const has = Boolean(af?.uri);
              const linked =
                has && af?.uri
                  ? findAssetForFile(af as unknown as AssetFileShape, assetsList)
                  : undefined;
              const ph = row?.placeholder_asset
                ? String(row.placeholder_asset)
                : def.placeholder_asset
                  ? String(def.placeholder_asset)
                  : undefined;
              const directThumb = assetsList.find(
                (a) => a.uri === (af?.uri as string | undefined),
              )?.thumbnail_url;
              const srcFallback = directThumb || ph;
              return (
                <div
                  key={`el-${i}-${name}`}
                  className={`aspectly:absolute aspectly:overflow-hidden aspectly:cursor-pointer aspectly:transition-shadow ${selectionRing}`}
                  style={{ ...box, zIndex: z }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFrameClick(i, typ);
                  }}
                  title={`${name} (${typeDisplay.label})`}
                >
                  <div className="aspectly:absolute aspectly:inset-0 aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-black/20">
                    {linked?.id != null && linked.thumbnail_url ? (
                      <AssetBinaryImage
                        assetId={linked.id}
                        endpoint="thumbnail"
                        remoteUrlHint={linked.thumbnail_url}
                        alt=""
                        className="aspectly:h-full aspectly:w-full aspectly:object-cover"
                        loadingClassName="aspectly:h-full aspectly:w-full aspectly:min-h-[48px] aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-black/20"
                      />
                    ) : srcFallback ? (
                      <img src={srcFallback} alt="" className="aspectly:h-full aspectly:w-full aspectly:object-cover" />
                    ) : (
                      <span className="material-symbols-outlined aspectly:text-primary aspectly:text-4xl">
                        play_circle
                      </span>
                    )}
                  </div>
                </div>
              );
            }

            // ── image (non-background) ──
            if (typ === 'image') {
              const af = asRecord(asRecord(data.image_element)?.asset_file);
              const has = Boolean(af?.uri);
              const linked =
                has && af?.uri
                  ? findAssetForFile(af as unknown as AssetFileShape, assetsList)
                  : undefined;
              const srcFallback = (af?.uri as string | undefined) || imagePreviewSrc(row, def);
              return (
                <div
                  key={`el-${i}-${name}`}
                  className={`aspectly:absolute aspectly:min-h-0 aspectly:min-w-0 aspectly:overflow-hidden aspectly:rounded-lg aspectly:cursor-pointer aspectly:transition-shadow ${selectionRing}`}
                  style={{ ...box, zIndex: z }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFrameClick(i, typ);
                  }}
                  title={`${name} (${typeDisplay.label})`}
                >
                  {linked?.id != null ? (
                    <AssetBinaryImage
                      assetId={linked.id}
                      endpoint={linked.thumbnail_url != null ? 'thumbnail' : 'content'}
                      remoteUrlHint={linked.thumbnail_url ?? linked.uri}
                      alt=""
                      className="aspectly:absolute aspectly:inset-0 aspectly:h-full aspectly:w-full aspectly:object-contain"
                      loadingClassName="aspectly:absolute aspectly:inset-0 aspectly:h-full aspectly:w-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-black/30"
                    />
                  ) : srcFallback ? (
                    <img
                      src={srcFallback}
                      alt=""
                      className="aspectly:absolute aspectly:inset-0 aspectly:h-full aspectly:w-full aspectly:object-contain"
                    />
                  ) : (
                    <div className="aspectly:absolute aspectly:inset-0 aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:gap-2 aspectly:bg-black/30">
                      <span className="material-symbols-outlined aspectly:text-3xl aspectly:text-white/20">
                        image
                      </span>
                      <span className="aspectly:text-[9px] aspectly:font-bold aspectly:uppercase aspectly:tracking-wider aspectly:text-white/30">
                        {name}
                      </span>
                    </div>
                  )}
                  {/* selected label badge */}
                  {isSelected && (
                    <div className="aspectly:absolute aspectly:top-1 aspectly:left-1 aspectly:z-10 aspectly:flex aspectly:items-center aspectly:gap-1 aspectly:rounded aspectly:bg-primary/80 aspectly:px-1.5 aspectly:py-0.5 aspectly:backdrop-blur-sm">
                      <span className="material-symbols-outlined aspectly:text-[11px] aspectly:leading-none aspectly:text-white">
                        image
                      </span>
                      <span className="aspectly:text-[9px] aspectly:font-bold aspectly:uppercase aspectly:tracking-wider aspectly:text-white">
                        {name}
                      </span>
                    </div>
                  )}
                </div>
              );
            }

            // ── video (non-background) ──
            if (typ === 'video') {
              const af = asRecord(asRecord(data.video_element)?.asset_file);
              const has = Boolean(af?.uri);
              const linked =
                has && af?.uri
                  ? findAssetForFile(af as unknown as AssetFileShape, assetsList)
                  : undefined;
              const ph = row?.placeholder_asset
                ? String(row.placeholder_asset)
                : def.placeholder_asset
                  ? String(def.placeholder_asset)
                  : undefined;
              const directThumb = assetsList.find(
                (a) => a.uri === (af?.uri as string | undefined),
              )?.thumbnail_url;
              const srcFallback = directThumb || ph;
              return (
                <div
                  key={`el-${i}-${name}`}
                  className={`aspectly:absolute aspectly:min-h-0 aspectly:min-w-0 aspectly:overflow-hidden aspectly:rounded-lg aspectly:cursor-pointer aspectly:transition-shadow ${selectionRing}`}
                  style={{ ...box, zIndex: z }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFrameClick(i, typ);
                  }}
                  title={`${name} (${typeDisplay.label})`}
                >
                  {linked?.id != null && linked.thumbnail_url ? (
                    <AssetBinaryImage
                      assetId={linked.id}
                      endpoint="thumbnail"
                      remoteUrlHint={linked.thumbnail_url}
                      alt=""
                      className="aspectly:absolute aspectly:inset-0 aspectly:h-full aspectly:w-full aspectly:object-contain"
                      loadingClassName="aspectly:absolute aspectly:inset-0 aspectly:h-full aspectly:w-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-black/30"
                    />
                  ) : srcFallback ? (
                    <img
                      src={srcFallback}
                      alt=""
                      className="aspectly:absolute aspectly:inset-0 aspectly:h-full aspectly:w-full aspectly:object-contain"
                    />
                  ) : (
                    <div className="aspectly:absolute aspectly:inset-0 aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:gap-2 aspectly:bg-black/30">
                      <span className="material-symbols-outlined aspectly:text-4xl aspectly:text-primary">
                        play_circle
                      </span>
                      <span className="aspectly:text-[9px] aspectly:font-bold aspectly:uppercase aspectly:tracking-wider aspectly:text-white/30">
                        {name}
                      </span>
                    </div>
                  )}
                  {isSelected && (
                    <div className="aspectly:absolute aspectly:top-1 aspectly:left-1 aspectly:z-10 aspectly:flex aspectly:items-center aspectly:gap-1 aspectly:rounded aspectly:bg-primary/80 aspectly:px-1.5 aspectly:py-0.5 aspectly:backdrop-blur-sm">
                      <span className="material-symbols-outlined aspectly:text-[11px] aspectly:leading-none aspectly:text-white">
                        videocam
                      </span>
                      <span className="aspectly:text-[9px] aspectly:font-bold aspectly:uppercase aspectly:tracking-wider aspectly:text-white">
                        {name}
                      </span>
                    </div>
                  )}
                </div>
              );
            }

            // ── text ──
            if (typ === 'text') {
              const textElement = asRecord(data.text_element);
              const rawText = String(asRecord(data.text_element)?.text_content ?? '');
              const textContent = /click to edit/i.test(rawText) ? '' : rawText;
              const fontSize = Number(textElement?.font_size ?? 16);
              const fontFamily =
                typeof textElement?.font_family === 'string'
                  ? textElement.font_family
                  : 'sans-serif';
              const color = typeof textElement?.color === 'string' ? textElement.color : '#ffffff';
              const isDarkText = isColorDark(color);
              const editBackgroundColor = isDarkText ? '#ffffff' : '#000000';
              const scaledFontSize = Math.max(1, fontSize * canvasScale);
              const renderedHtml = sanitizeMarkdownHtml(
                marked.parse(textContent, { async: false }) as string,
              );
              return (
                <div
                  key={`el-${i}-${name}`}
                  className={`aspectly:absolute aspectly:min-h-0 aspectly:min-w-0 aspectly:overflow-hidden aspectly:rounded-lg aspectly:cursor-pointer aspectly:transition-shadow aspectly:flex aspectly:items-center aspectly:justify-center ${selectionRing}`}
                  style={{
                    ...box,
                    zIndex: z,
                    color,
                    fontSize: scaledFontSize,
                    fontFamily,
                    textAlign: 'center',
                    wordBreak: 'break-word',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFrameClick(i, typ);
                  }}
                  title={isTextEditing ? undefined : `${name} — click to edit`}
                >
                  {/* Rendered markdown fills frame */}
                  <div
                    className="aspectly:absolute aspectly:inset-0 aspectly:flex aspectly:items-center aspectly:justify-center aspectly:overflow-hidden aspectly:text-center aspectly:[&_p]:m-0 aspectly:[&_p]:text-center aspectly:[&_a]:text-primary aspectly:[&_code]:text-primary"
                    dangerouslySetInnerHTML={{ __html: renderedHtml }}
                  />

                  {/* Inline textarea overlay when editing */}
                  {isTextEditing && (
                    <textarea
                      autoFocus
                      value={textContent}
                      onChange={(e) => {
                        patchEl(i, (el) => {
                          const d = asRecord(el.element_data) ?? {};
                          const te = asRecord(d.text_element) ?? {};
                          te.text_content = e.target.value;
                          d.text_element = te;
                          el.element_data = d;
                        });
                      }}
                      onBlur={() => setTextEditingIndex(null)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setTextEditingIndex(null);
                        }
                      }}
                      placeholder="Markdown text…"
                      className={`aspectly:absolute aspectly:inset-0 aspectly:resize-none aspectly:rounded-lg aspectly:p-3 aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/60 aspectly:z-10 ${
                        isDarkText ? 'aspectly:placeholder:text-black/40' : 'aspectly:placeholder:text-white/30'
                      }`}
                      style={{
                        fontFamily,
                        fontSize: scaledFontSize,
                        color,
                        backgroundColor: editBackgroundColor,
                      }}
                      spellCheck
                    />
                  )}
                </div>
              );
            }

            // ── qr_code ──
            if (typ === 'qr_code') {
              return (
                <div
                  key={`el-${i}-${name}`}
                  className={`aspectly:absolute aspectly:min-h-0 aspectly:min-w-0 aspectly:overflow-hidden aspectly:rounded-lg aspectly:cursor-pointer aspectly:transition-shadow aspectly:bg-black/20 ${selectionRing}`}
                  style={{ ...box, zIndex: z }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFrameClick(i, typ);
                  }}
                  title={`${name} (${typeDisplay.label})`}
                >
                  <div className="aspectly:absolute aspectly:inset-0 aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-white">
                    <span
                      className="material-symbols-outlined aspectly:text-black"
                      style={{ fontSize: 'clamp(32px, 8vw, 120px)' }}
                    >
                      qr_code_2
                    </span>
                  </div>
                  {isSelected && (
                    <div className="aspectly:absolute aspectly:top-1 aspectly:left-1 aspectly:z-10 aspectly:flex aspectly:items-center aspectly:gap-1 aspectly:rounded aspectly:bg-primary/80 aspectly:px-1.5 aspectly:py-0.5 aspectly:backdrop-blur-sm">
                      <span className="material-symbols-outlined aspectly:text-[11px] aspectly:leading-none aspectly:text-white">
                        qr_code
                      </span>
                      <span className="aspectly:text-[9px] aspectly:font-bold aspectly:uppercase aspectly:tracking-wider aspectly:text-white">
                        {name}
                      </span>
                    </div>
                  )}
                </div>
              );
            }

            // ── fallback: unknown element type ──
            return (
              <div
                key={`el-${i}-${name}`}
                className={`aspectly:absolute aspectly:min-h-0 aspectly:min-w-0 aspectly:overflow-hidden aspectly:rounded-lg aspectly:cursor-pointer aspectly:transition-shadow aspectly:bg-black/20 ${selectionRing}`}
                style={{ ...box, zIndex: z }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFrameClick(i, typ);
                }}
                title={`${name} (${typeDisplay.label})`}
              >
                <div className="aspectly:absolute aspectly:inset-0 aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:gap-1">
                  <span
                    className="material-symbols-outlined aspectly:text-white/20"
                    style={{ fontSize: 'clamp(20px, 3vw, 40px)' }}
                  >
                    {typeDisplay.materialSymbol}
                  </span>
                  <span className="aspectly:text-[9px] aspectly:font-bold aspectly:uppercase aspectly:tracking-wider aspectly:text-white/20">
                    {name}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
